import React, { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, CheckCircle, Copy, Share, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Separator } from '../../ui/separator';
import { Alert, AlertDescription } from '../../ui/alert';
import { apiClient } from '../../../services/apiClient';
import { formatPinForDisplay, pinToClipboardValue } from '../../../utils/formatPin';
import { APP_CONFIG } from '../../../config/app-config';
import { getToken as getSessionToken } from '../../../utils/authToken';
import { BrandSpinner } from '../../common/LoadingSpinner';
import { PaymentProceedModal, type PaymentProceedPayload } from '../shared/PaymentProceedModal';
import { randsToCents } from '../../../utils/formatCurrency';

interface FlashVoucherData {
  amount: number;
  reference: string;
  accountNumber: string;
  productCode: string;
}

interface PricingInfo {
  faceValue: number;
  commission: number;
  totalPayable: number;
  commissionRate: number;
}

type Step = 'form' | 'processing' | 'success' | 'error';

const EEZI_CASH_WITHDRAWAL_ENABLED =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_EEZI_CASH_WITHDRAWAL_ENABLED) === 'true';

export function FlashEeziCashOverlay() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!EEZI_CASH_WITHDRAWAL_ENABLED) {
      navigate('/withdraw-cash-overlay', { replace: true });
    }
  }, [navigate]);

  if (!EEZI_CASH_WITHDRAWAL_ENABLED) {
    return (
      <div className="p-6 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <p className="text-gray-700 mb-4">Flash eeziCash withdraw is temporarily unavailable.</p>
        <p className="text-sm text-gray-600 mb-4">Use Withdraw Cash for Nedbank or ABSA CashSend PIN.</p>
        <BrandSpinner label="Opening Withdraw Cash" />
      </div>
    );
  }
  
  // Form state
  const [amount, setAmount] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<Step>('form');
  // Universal pre-debit proceed confirmation.
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [proceedPayload, setProceedPayload] = useState<PaymentProceedPayload | null>(null);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [errors, setErrors] = useState<{ amount?: string; balance?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [copiedPIN, setCopiedPIN] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Success state
  const [voucherToken, setVoucherToken] = useState<string>('');
  const [transactionRef, setTransactionRef] = useState<string>('');

  // System fields (read-only) — accountNumber and productCode are resolved server-side
  const systemData = {
    reference: `EZ${Date.now().toString().slice(-8).toUpperCase()}`
  };

  // Quick amount options
  const quickAmounts = [50, 100, 150, 200, 300, 400, 500];
  
  // Transaction fee
  const TRANSACTION_FEE = 8.00;

  // Fetch wallet balance on mount
  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const token = getSessionToken();
        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/wallets/balance`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setWalletBalance(parseFloat(data.data.balance || 0));
        }
      } catch (error) {
        console.error('Failed to fetch wallet balance:', error);
      }
    };
    
    fetchWalletBalance();
  }, []);

  // Amount validation
  const validateAmount = (value: string): boolean => {
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue >= 50 && numValue <= 500;
  };

  // Calculate pricing based on amount
  const calculatePricing = (amountValue: number): PricingInfo => {
    // Flat transaction fee: R8.00 (VAT Inclusive)
    const transactionFee = 8.00;
    
    return {
      faceValue: amountValue,
      commission: transactionFee,
      totalPayable: amountValue + transactionFee,
      commissionRate: 0 // Not used - flat fee instead
    };
  };

  // Update pricing when amount changes
  useEffect(() => {
    if (amount && validateAmount(amount)) {
      const amountValue = parseFloat(amount);
      setPricing(calculatePricing(amountValue));
      
      // Check wallet balance
      if (walletBalance !== null) {
        const totalRequired = amountValue + TRANSACTION_FEE;
        if (totalRequired > walletBalance) {
          setErrors(prev => ({ 
            ...prev, 
            balance: `Insufficient balance. Required: R${totalRequired.toFixed(2)}, Available: R${walletBalance.toFixed(2)}` 
          }));
        } else {
          setErrors(prev => ({ ...prev, balance: undefined }));
        }
      }
    } else {
      setPricing(null);
    }
  }, [amount, walletBalance]);

  // Handle amount input
  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setAmount(cleanValue);
    
    // Clear amount error if valid
    if (validateAmount(cleanValue)) {
      setErrors(prev => ({ ...prev, amount: undefined }));
    }
  };


  // Handle quick amount selection
  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount.toString());
    setErrors(prev => ({ ...prev, amount: undefined }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { amount?: string; balance?: string } = {};
    
    if (!amount || !validateAmount(amount)) {
      newErrors.amount = 'Amount must be between R50 and R500';
    }
    
    if (pricing && walletBalance !== null && pricing.totalPayable > walletBalance) {
      newErrors.balance = `Insufficient balance. Required: R${pricing.totalPayable.toFixed(2)}, Available: R${walletBalance.toFixed(2)}`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle copy PIN — raw value, no spaces (display is grouped, copy is not).
  const handleCopyPIN = async () => {
    try {
      await navigator.clipboard.writeText(pinToClipboardValue(voucherToken));
      setCopiedPIN(true);
      setTimeout(() => setCopiedPIN(false), 2000);
    } catch (err) {
      console.error('Failed to copy PIN:', err);
    }
  };

  // Build the proceed payload from the current form + pricing and open the
  // universal confirm modal. The actual API call stays in handleSubmit.
  const prepareProceed = () => {
    if (!validateForm()) return;
    const faceValue = pricing?.faceValue ?? parseFloat(amount || '0');
    const feeCents = pricing ? randsToCents(Math.max(pricing.totalPayable - pricing.faceValue, 0)) : undefined;
    const totalCents = pricing ? randsToCents(pricing.totalPayable) : randsToCents(faceValue);
    const payload: PaymentProceedPayload = {
      amountCents: randsToCents(faceValue),
      recipientLabel: 'cash withdrawal PIN',
      contextLabel: 'Cardless cash withdrawal rail',
      ...(typeof feeCents === 'number' ? { feeAmountCents: feeCents } : {}),
      ...(typeof totalCents === 'number' ? { totalDebitCents: totalCents } : {}),
      title: 'Confirm cash withdrawal',
      proceedButtonText: 'Yes, proceed',
    };
    setProceedPayload(payload);
    setShowProceedModal(true);
  };

  const cancelProceed = () => {
    setProceedPayload(null);
    setShowProceedModal(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setShowProceedModal(false);
    setProceedPayload(null);
    setCurrentStep('processing');
    
    try {
      // Prepare request data — accountNumber and productCode are resolved server-side from env
      const requestData = {
        amount: Math.round(parseFloat(amount) * 100), // Convert to cents
        reference: systemData.reference,
        metadata: {
          source: 'FlashEeziCashOverlay',
          timestamp: new Date().toISOString()
        }
      };
      
      console.log('🚀 Flash eeziCash: Calling API with data:', requestData);
      
      // Call Flash eezi-voucher purchase API
      const response = await apiClient.post('/api/v1/flash/eezi-voucher/purchase', requestData);
      
      console.log('✅ Flash eeziCash: API response:', response);
      
      // Extract token/PIN from Flash response
      // Response format: { success: true, data: { transaction: { ... } } }
      const transaction = (response as any).data?.transaction || (response as any).data;
      const token = transaction?.pin || transaction?.token || transaction?.serialNumber || transaction?.reference;
      const ref = transaction?.transactionId || transaction?.reference || systemData.reference;
      
      if (!token) {
        console.error('❌ Flash eeziCash: No token in response:', transaction);
        throw new Error('No cash-withdrawal PIN received from Flash API');
      }
      
      setVoucherToken(token);
      setTransactionRef(ref);
      setCurrentStep('success');
      
      console.log('🎉 Flash eeziCash: Success - Token:', token, 'Ref:', ref);
      
    } catch (error: any) {
      console.error('❌ Flash eeziCash: Transaction failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      const errorMsg = error?.response?.data?.message || error?.message || 'Transaction failed. Please try again or contact support.';
      setErrorMessage(errorMsg);
      setCurrentStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy cash PIN — raw value, no spaces.
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(pinToClipboardValue(voucherToken));
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Form can be submitted  
  const canSubmit = amount && validateAmount(amount) && !isSubmitting && !errors.balance;

  // Main container for all steps
  if (currentStep === 'success') {
    return (
      <div 
        style={{ padding: '1rem' }}
        role="dialog"
        aria-labelledby="success-title"
        aria-describedby="success-description"
      >
        <div id="success-description" className="sr-only">
          Your eeziCash cash-withdrawal PIN has been created and is ready to use at Flash traders.
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/transact')}
            style={{
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px'
            }}
            aria-label="Back to transact"
          >
            <ArrowLeft style={{ width: '20px', height: '20px' }} />
          </Button>
          
          <h1 
            id="success-title"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937'
            }}
          >
            Cash PIN Created
          </h1>
          
          <div style={{ width: '44px' }}></div>
        </div>

        {/* Success Card */}
        <Card style={{
          backgroundColor: '#ffffff',
          border: '1px solid #16a34a',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(22, 163, 74, 0.1)'
        }}>
          <CardHeader style={{
            backgroundColor: '#16a34a',
            color: '#ffffff',
            borderRadius: '12px 12px 0 0'
          }}>
            <div className="flex items-center gap-3">
              <CheckCircle style={{ width: '24px', height: '24px' }} />
              <div>
                <CardTitle style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ffffff'
                }}>
                  eeziCash PIN Ready
                </CardTitle>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#ffffff',
                  opacity: 0.9
                }}>
                  {new Date().toLocaleString()}
                </p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent style={{ padding: '1rem' }}>
            <div className="space-y-4">
              {/* Cash PIN */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8fafe',
                borderRadius: '12px',
                border: '1px dashed #86BE41'
              }}>
                <div className="text-center">
                  <Label style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    Cash PIN
                  </Label>
                  <p style={{
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: '8px 0',
                    letterSpacing: '3px'
                  }}>
                    {formatPinForDisplay(voucherToken)}
                  </p>
                  <Button
                    onClick={handleCopyCode}
                    size="sm"
                    style={{
                      backgroundColor: '#86BE41',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px'
                    }}
                  >
                    <Copy style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                    Copy Code
                  </Button>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Reference
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {transactionRef}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Face Value
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    R{pricing?.faceValue}
                  </span>
                </div>

              </div>

              {/* How to Cash Out */}
              <Alert style={{
                backgroundColor: '#e0f2fe',
                borderColor: '#2D8CCA',
                borderRadius: '12px'
              }}>
                <Info style={{ width: '16px', height: '16px', color: '#2D8CCA' }} />
                <AlertDescription>
                  <div>
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      fontWeight: '500',
                      marginBottom: '8px',
                      color: '#0c4a6e'
                    }}>
                      How to cash out:
                    </p>
                    <ol style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '11px',
                      color: '#0c4a6e',
                      paddingLeft: '16px',
                      margin: 0
                    }}>
                      <li>Visit any participating Flash trader</li>
                      <li>Present your cash PIN: {voucherToken}</li>
                      <li>Provide valid ID for verification</li>
                      <li>Receive your cash (R{pricing?.faceValue})</li>
                    </ol>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Done Button */}
              <Button
                onClick={() => navigate('/transact')}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  minHeight: '44px'
                }}
              >
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'processing') {
    return (
      <div 
        style={{ padding: '1rem' }}
        role="dialog"
        aria-labelledby="processing-title"
        aria-describedby="processing-description"
      >
        <div id="processing-description" className="sr-only">
          Your eeziCash cash-withdrawal request is being processed. Please wait.
        </div>
        <h2 id="processing-title" className="sr-only">
          Processing Your Purchase
        </h2>

        <BrandSpinner
          className="py-8"
          size={56}
          label="Processing Your Purchase"
          subtitle="Creating your eeziCash cash-withdrawal PIN..."
        />
      </div>
    );
  }

  return (
    <div 
      style={{ padding: '1rem' }}
      role="dialog"
      aria-labelledby="overlay-title"
      aria-describedby="overlay-description"
    >
      <div id="overlay-description" className="sr-only">
        Create an eeziCash cash-withdrawal PIN for use at Flash traders.
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', position: 'relative' }}>
        <Button
          variant="ghost"
          onClick={() => navigate('/transact')}
          style={{
            padding: '8px',
            minWidth: '44px',
            minHeight: '44px',
            position: 'absolute',
            left: '0',
            zIndex: 1
          }}
          aria-label="Back to transact"
        >
          <ArrowLeft style={{ width: '20px', height: '20px' }} />
        </Button>
        
        <div style={{ 
          flex: 1, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h1 
            id="overlay-title"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}
          >
            Cash @ Flash (eeziCash)
          </h1>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '12px',
            color: '#6b7280',
            margin: 0
          }}>
            Create a cash PIN for instant cash withdrawal
          </p>
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200 mb-6">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: 'var(--mobile-font-small)',
          color: '#1e40af'
        }}>
          <strong>How it works:</strong> Create a cash-withdrawal credential, visit any Flash trader, show the PIN, and receive cash.
        </AlertDescription>
      </Alert>

      {/* Wallet Balance Display */}
      {walletBalance !== null && (
        <Card className="bg-gradient-to-r from-[#86BE41]/10 to-[#2D8CCA]/10 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#86BE41]" />
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'var(--mobile-font-small)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: '#374151'
                }}>
                  Available Balance
                </span>
              </div>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: '#1f2937'
              }}>
                R{walletBalance.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insufficient Balance Error */}
      {errors.balance && (
        <Alert className="bg-red-50 border-red-200 mb-6">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'var(--mobile-font-small)',
            color: '#dc2626'
          }}>
            {errors.balance}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Form Card */}
      <Card style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <CardHeader>
          <CardTitle style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '16px',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            Create eeziCash Cash PIN
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Amount Section */}
          <div className="space-y-3">
            <Label 
              htmlFor="amount"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1f2937'
              }}
            >
              Withdrawal Amount *
            </Label>
            
            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {quickAmounts.map((quickAmount) => (
                <Button
                  key={quickAmount}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAmount(quickAmount)}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    minHeight: '36px',
                    backgroundColor: amount === quickAmount.toString() ? '#86BE41' : 'transparent',
                    color: amount === quickAmount.toString() ? '#ffffff' : '#6b7280',
                    borderColor: amount === quickAmount.toString() ? '#86BE41' : '#e2e8f0'
                  }}
                >
                  R{quickAmount}
                </Button>
              ))}
            </div>

            <div className="relative">
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                color: '#6b7280',
                zIndex: 1
              }}>
                R
              </span>
              <Input
                id="amount"
                type="text"
                inputMode="decimal"
                placeholder="50"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                onKeyDown={(e) => {
                  // Banking-grade: Prevent browser auto-formatting quirks
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => {
                  // Banking-grade: Prevent scroll-to-change number input values
                  e.currentTarget.blur();
                }}
                style={{
                  paddingLeft: '28px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '12px',
                  minHeight: '44px',
                  border: errors.amount ? '1px solid #dc2626' : '1px solid #e2e8f0'
                }}
                aria-describedby={errors.amount ? 'amount-error' : 'amount-help'}
              />
            </div>

            <p 
              id="amount-help"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '12px',
                color: '#6b7280'
              }}
            >
              Minimum R50, Maximum R500
            </p>

            {errors.amount && (
              <p 
                id="amount-error"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#dc2626'
                }}
                role="alert"
              >
                {errors.amount}
              </p>
            )}
          </div>

          {/* Pricing Summary */}
          {pricing && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8fafe',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '12px'
              }}>
                Pricing Summary
              </h3>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Face value
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    R{pricing.faceValue.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Transaction Fee
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    R{pricing.commission.toFixed(2)}
                  </span>
                </div>

                <Separator />

                <div className="flex justify-between">
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1f2937'
                  }}>
                    Total payable
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1f2937'
                  }}>
                    R{pricing.totalPayable.toFixed(2)}
                  </span>
                </div>
              </div>

              <p style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '11px',
                color: '#6b7280',
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                Transaction fee: R8.00 flat rate (VAT Inclusive)
              </p>
            </div>
          )}

          {/* Error Display */}
          {currentStep === 'error' && (
            <Alert style={{
              backgroundColor: '#fee2e2',
              borderColor: '#dc2626',
              borderRadius: '12px'
            }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#dc2626' }} />
              <AlertDescription>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#991b1b'
                }}>
                  {errorMessage || 'Transaction failed. Please try again or contact support.'}
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/transact')}
              style={{
                flex: '1',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                minHeight: '44px',
                borderRadius: '12px'
              }}
            >
              Cancel
            </Button>
            
            <Button
              onClick={prepareProceed}
              disabled={!canSubmit}
              className="flex items-center justify-center gap-2"
              style={{
                flex: '2',
                background: canSubmit
                  ? 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)'
                  : '#e2e8f0',
                color: canSubmit ? '#ffffff' : '#6b7280',
                border: 'none',
                borderRadius: '12px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '600',
                minHeight: '48px',
                padding: '12px 20px',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap'
              }}
            >
              <CreditCard style={{ width: '18px', height: '18px', flexShrink: 0 }} />
              <span>Buy eeziCash</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <PaymentProceedModal
        open={showProceedModal}
        payload={proceedPayload}
        onProceed={handleSubmit}
        onCancel={cancelProceed}
        isProcessing={isSubmitting}
      />
    </div>
  );
}


